import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { BsArrowLeft } from "react-icons/bs";
import Cabecalho from "../../components/Cabecalho/Cabecalho";
import ImprimirBoletimButton from "../../components/ImprimirBoletimButton";
import { BoletimContext } from "../../context/BoletimContext";
import api from "../../Utils/api";
import { hasPermission } from "../../Utils/permissions";

const PAGE_SIZE = 10;
const EMPTY_FILTERS = {
  numero: "",
  dataInicio: "",
  dataFim: "",
  municipio: "",
  natureza: "",
  bairro: "",
};

function UserProfile() {
  const navigate = useNavigate();
  const { setBoletim } = useContext(BoletimContext);
  const [userData, setUserData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(null);
  const [reportsPage, setReportsPage] = useState(0);
  const [hasMoreReports, setHasMoreReports] = useState(false);
  const [hasSearchedReports, setHasSearchedReports] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [reportActionError, setReportActionError] = useState("");
  const [retryReportsPage, setRetryReportsPage] = useState(1);
  const requestInProgress = useRef(false);

  const loadReports = useCallback(async (page, append = false, submittedFilters = activeFilters) => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    setReportsError("");
    setRetryReportsPage(page);
    if (append) setLoadingMore(true);
    else {
      setLoadingReports(true);
      setActiveFilters(submittedFilters);
    }

    try {
      const params = { page, limit: PAGE_SIZE };
      Object.entries(submittedFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const { data } = await api.get("/user/me/incident-reports", { params });
      setReports((current) => {
        const existingIds = new Set(append ? current.map((report) => report._id) : []);
        const nextReports = append
          ? [...current, ...data.reports.filter((report) => !existingIds.has(report._id))]
          : data.reports;
        return nextReports;
      });
      setReportsPage(data.page);
      setHasMoreReports(data.hasMore);
      setTotalReports(Number.isFinite(data.total) ? data.total : null);
      setHasSearchedReports(true);
    } catch (requestError) {
      setReportsError(requestError.response?.data?.message || "Não foi possível carregar seus boletins.");
    } finally {
      requestInProgress.current = false;
      setLoadingReports(false);
      setLoadingMore(false);
    }
  }, [activeFilters]);

  useEffect(() => {
    api.get("/user/me")
      .then(({ data }) => setUserData(data))
      .catch((requestError) => setError(requestError.response?.data?.message || "Não foi possível carregar o perfil."))
      .finally(() => setLoadingProfile(false));
  }, []);

  const updateField = (field) => (event) =>
    setUserData((current) => ({ ...current, [field]: event.target.value }));

  const updateFilter = (field) => (event) =>
    setFilters((current) => ({ ...current, [field]: event.target.value }));
  const filtersChanged = Object.keys(filters).some((field) => filters[field] !== activeFilters[field]);

  const searchReports = (event) => {
    event.preventDefault();
    setReports([]);
    setReportsPage(0);
    setHasMoreReports(false);
    setTotalReports(null);
    loadReports(1, false, filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setReports([]);
    setReportsPage(0);
    setHasMoreReports(false);
    setTotalReports(null);
    loadReports(1, false, EMPTY_FILTERS);
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put("/user/me", {
        userName: userData.userName,
        userEmail: userData.userEmail,
        userContato: userData.userContato,
        userMatriculaId: userData.userMatriculaId,
        userGraduacao: userData.userGraduacao,
        userBarra: userData.userBarra,
      });
      setUserData(data.user);
      if (data.user.userMatriculaId) {
        localStorage.setItem("x-user-mat-id", data.user.userMatriculaId);
      } else {
        localStorage.removeItem("x-user-mat-id");
      }
      setSuccess(data.message);
      if (hasSearchedReports) loadReports(1, false, activeFilters);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível atualizar o perfil.");
    } finally {
      setSaving(false);
    }
  };

  const openReport = async (report, destination) => {
    setReportActionError("");
    try {
      const { data } = await api.get(`/incidentReport/${encodeURIComponent(report._id)}`);
      setBoletim({
        ...data,
        envolvidos: data.envolvidos || [],
        materiaisApreendidos: data.materiaisApreendidos || [],
        efetivo: data.efetivo || [],
      });
      navigate(destination);
    } catch (requestError) {
      setReportActionError(requestError.response?.data?.message || "Não foi possível abrir o boletim.");
    }
  };

  const canViewReports = hasPermission("boletins.view.own") || hasPermission("boletins.view.all");
  const canPrintReports = hasPermission("boletins.print");

  return (
    <>
      <Cabecalho />
      <Container className="py-4">
        <Row className="justify-content-center g-4">
          <Col xs={12} lg={10}>
            <Card>
              <Card.Header><Card.Title className="mb-0">Meu perfil</Card.Title></Card.Header>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                {loadingProfile ? (
                  <div className="text-center py-4"><Spinner animation="border" role="status" /></div>
                ) : userData ? (
                  <Form onSubmit={saveProfile}>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group controlId="profile-user-name">
                          <Form.Label>Nome</Form.Label>
                          <Form.Control required value={userData.userName || ""} onChange={updateField("userName")} />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group controlId="profile-user-matricula">
                          <Form.Label>Matrícula</Form.Label>
                          <Form.Control value={userData.userMatriculaId || ""} onChange={updateField("userMatriculaId")} />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group controlId="profile-user-graduation">
                          <Form.Label>Posto / Graduação</Form.Label>
                          <Form.Control value={userData.userGraduacao || ""} onChange={updateField("userGraduacao")} />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group controlId="profile-user-barra">
                          <Form.Label>Número / Barra</Form.Label>
                          <Form.Control value={userData.userBarra || ""} onChange={updateField("userBarra")} />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group controlId="profile-user-email">
                          <Form.Label>E-mail</Form.Label>
                          <Form.Control required type="email" value={userData.userEmail || ""} onChange={updateField("userEmail")} />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group controlId="profile-user-contact">
                          <Form.Label>Contato</Form.Label>
                          <Form.Control value={userData.userContato || ""} onChange={updateField("userContato")} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-grid d-sm-flex justify-content-sm-end mt-3">
                      <Button type="submit" disabled={saving}>
                        {saving ? "Salvando..." : "Salvar perfil"}
                      </Button>
                    </div>
                  </Form>
                ) : null}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={10}>
            <Card>
              <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <Card.Title className="mb-0">Meus boletins no efetivo</Card.Title>
                {hasSearchedReports && (
                  <Badge bg="secondary">
                    {reports.length} exibidos{totalReports !== null ? ` de ${totalReports}` : ""}
                  </Badge>
                )}
              </Card.Header>
              <Card.Body>
                {!canViewReports ? (
                  <Alert variant="warning" className="mb-0">Sua função não possui permissão para consultar boletins.</Alert>
                ) : (
                  <>
                    <Form onSubmit={searchReports} className="mb-3">
                      <Row className="g-3">
                        <Col xs={12} sm={6} md={4}>
                          <Form.Group controlId="profile-report-number">
                            <Form.Label>Número do boletim</Form.Label>
                            <Form.Control value={filters.numero} onChange={updateFilter("numero")} />
                          </Form.Group>
                        </Col>
                        <Col xs={6} sm={3} md={4}>
                          <Form.Group controlId="profile-report-date-start">
                            <Form.Label>Data inicial</Form.Label>
                            <Form.Control type="date" value={filters.dataInicio} onChange={updateFilter("dataInicio")} />
                          </Form.Group>
                        </Col>
                        <Col xs={6} sm={3} md={4}>
                          <Form.Group controlId="profile-report-date-end">
                            <Form.Label>Data final</Form.Label>
                            <Form.Control type="date" value={filters.dataFim} onChange={updateFilter("dataFim")} />
                          </Form.Group>
                        </Col>
                        {[
                          ["municipio", "Município"],
                          ["natureza", "Natureza"],
                          ["bairro", "Bairro"],
                        ].map(([field, label]) => (
                          <Col xs={12} sm={6} md={4} key={field}>
                            <Form.Group controlId={`profile-filter-${field}`}>
                              <Form.Label>{label}</Form.Label>
                              <Form.Control value={filters[field]} onChange={updateFilter(field)} />
                            </Form.Group>
                          </Col>
                        ))}
                      </Row>
                      <div className="d-grid d-sm-flex gap-2 mt-3">
                        {!hasSearchedReports && (
                          <Button type="submit" disabled={loadingReports}>
                            {loadingReports ? <><Spinner size="sm" className="me-2" />Carregando...</> : "Carregar meus boletins"}
                          </Button>
                        )}
                        {hasSearchedReports && (
                          <>
                            <Button type="submit" disabled={loadingReports || loadingMore}>
                              {loadingReports ? <><Spinner size="sm" className="me-2" />Buscando...</> : "Buscar"}
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={clearFilters} disabled={loadingReports || loadingMore}>
                              Limpar filtros
                            </Button>
                          </>
                        )}
                      </div>
                    </Form>

                    {reportsError && (
                      <Alert variant="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>{reportsError}</span>
                        <Button variant="outline-danger" size="sm" onClick={() => loadReports(retryReportsPage, retryReportsPage > 1, activeFilters)}>
                          Tentar novamente
                        </Button>
                      </Alert>
                    )}
                    {reportActionError && <Alert variant="danger">{reportActionError}</Alert>}

                    {loadingReports && (
                      <div className="text-center py-4">
                        <Spinner animation="border" role="status" />
                        <div className="mt-2">Carregando boletins...</div>
                      </div>
                    )}
                    {hasSearchedReports && !loadingReports && !reportsError && reports.length === 0 && (
                      <p className="text-center text-muted mb-0 py-3">Nenhum boletim encontrado no seu efetivo.</p>
                    )}
                    {reports.length > 0 && (
                      <div className="d-grid gap-3">
                        {reports.map((report) => {
                          const canEdit = hasPermission("boletins.edit.all") ||
                            (report.isCreator && hasPermission("boletins.edit.own"));
                          return (
                            <Card key={report._id} className="h-100">
                              <Card.Body>
                                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                                  <div className="flex-grow-1">
                                    <Card.Title as="h6" className="mb-2">
                                      B.O. nº {report.numero || "—"} · {report.natureza || "Natureza não informada"}
                                    </Card.Title>
                                    <div className="text-muted small">
                                      {report.data || "Data não informada"}{report.horario ? ` às ${report.horario}` : ""}
                                      {" · "}{report.municipio || "Município não informado"}
                                      {report.bairro ? ` · ${report.bairro}` : ""}
                                    </div>
                                    {report.endereco && (
                                      <div className="small mt-1">
                                        {report.endereco}{report.numeroEndereco ? `, ${report.numeroEndereco}` : ""}
                                      </div>
                                    )}
                                  </div>
                                  {report.isCreator && <Badge bg="primary">Registrado por você</Badge>}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => openReport(report, "/BoFromBD")}
                                  >
                                    Visualizar
                                  </Button>
                                  {canEdit && (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => openReport(report, "/boletim/header")}
                                    >
                                      Editar
                                    </Button>
                                  )}
                                  {canPrintReports && (
                                    <ImprimirBoletimButton
                                      report={report}
                                      buttonLabel="Imprimir novamente"
                                    />
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          );
                        })}
                      </div>
                    )}

                    {hasMoreReports && !loadingReports && (
                      <div className="d-grid d-sm-flex justify-content-sm-center mt-3">
                        <Button
                          variant="outline-primary"
                          onClick={() => loadReports(reportsPage + 1, true, activeFilters)}
                          disabled={loadingMore || filtersChanged}
                        >
                          {loadingMore ? <><Spinner size="sm" className="me-2" />Carregando...</> : "Carregar mais 10"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <div className="text-center mt-3">
          <Button as={Link} to="/dashboard" variant="outline-secondary">
            <BsArrowLeft className="me-2" /> Voltar ao painel
          </Button>
        </div>
      </Container>
    </>
  );
}

export default UserProfile;
